
import {useState,useEffect,useRef} from 'react'
import LangSwitch, { withLangLabel } from '../components/ui/LangSwitch';
import { validateBilingual } from '../utils/localized';
import { useNavigate, useParams } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { CardHeaderWithActions, MwInputText, MwSelector, MwTextArea,MwInputFile, MwButton, MwSpinnerButton, AlertMsg } from '../components/ui'
import { getAllowedProducts, getDefaultStatusId, getLocalizedOptions, getPriorityOptions, normalizeSupportSettings } from '../constants/ticket'
import { useCurrent, useFetch, usePost } from '../hooks';
import { useTranslation } from 'react-i18next';
import { apiClient as axios, buildAuthRequestConfig } from '../utils/apiClient';
import { FaTimes } from 'react-icons/fa';

const LARGE_UPLOAD_REQUEST_CONFIG = {
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    timeout: 0,
};

const stripHtmlText = (value) =>
    String(value || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const TicketCreate = () => {
    const [t] = useTranslation('global')
    const navigate = useNavigate();
    const params = useParams();
    const {user,currentBranchId,currentCompanyId,currentTenantId,currentLangId} = useCurrent();
    const {postData:generateTicketNumber} = usePost();
    const {data:resultSubmit,loading:loadingSubmit,postData:submitTicket} = usePost(false);
    const { data: supportSettingsResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/settings`);
    const { data: statusesResult } = useFetch(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}/statuses`);
    const [fileLoading,setFileLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const fileInputId = 'support_ticket_create_attachment';

    const [productSelected,setProductSelected] = useState();
    const [sectionSelected,setSectionSelected] = useState();
    const [prioritySelected,setPrioritySelected] = useState();
    const [subject,setSubject] = useState('');
    const [content,setContent] = useState('');
    // النسخة الإنجليزية + اللغة الظاهرة في الحقول.
    const [subjectEn,setSubjectEn] = useState('');
    const [contentEn,setContentEn] = useState('');
    const [formLang,setFormLang] = useState(currentLangId === 'en' ? 'en' : 'ar');
    const [ticketCreatedAt, setTicketCreatedAt] = useState('');
    const [attchment,setAttchment] = useState([]);
    const [createdTicketNumber,setCreatedTicketNumber] = useState(null);
    const [reservedTicketNumber,setReservedTicketNumber] = useState(null);
    const [reservedUserSequence,setReservedUserSequence] = useState(null);
    const [errors,setErrors] = useState();
    const reservedTicketRequestUserRef = useRef(null);
    const supportSettingsBase = normalizeSupportSettings(supportSettingsResult);
    const supportSettings = {
        ...supportSettingsBase,
        statuses: normalizeSupportSettings({ data: { statuses: statusesResult?.data || [] } }).statuses
    };
    const sectionOptions = getLocalizedOptions(supportSettings.sections, currentLangId);
    const productOptions = getLocalizedOptions(
        getAllowedProducts(supportSettings.products, user?.supportProductIds, {
            fallbackToAll: user?.role === 'admin'
        }),
        currentLangId
    );
    const priorityOptions = getPriorityOptions(currentLangId);

    const getUploadedLocations = (payload) => {
        const values = [
            payload?.location,
            payload?.data?.location,
            payload?.locations,
            payload?.data?.locations,
            payload?.files,
            payload?.data?.files,
        ];
        return values
            .flatMap((value) => Array.isArray(value) ? value : [value])
            .map((value) => typeof value === 'string' ? value : (value?.location || value?.url || value?.path || ''))
            .filter(Boolean);
    };

    const uploadHandler = async (e) => {
        const files = Array.from(e.target.files || []);
        if(!files.length) return;
        setErrors((state) => ({ ...state, attachment: undefined }));
        setFileLoading(true);
        try {
            const uploadedLocations = [];
            for (const file of files) {
                const _formData = new FormData();
                _formData.append('myfile',file,file.name);
                const response = await axios.post(
                    `${process.env.REACT_APP_UPLOADS_API_BASE_URL}/support`,
                    _formData,
                    buildAuthRequestConfig(LARGE_UPLOAD_REQUEST_CONFIG)
                );
                uploadedLocations.push(...getUploadedLocations(response.data));
            }
            setAttchment((state) => [...(Array.isArray(state) ? state : state ? [state] : []), ...uploadedLocations]);
            e.target.value = '';
        } catch {
            setErrors((state) => ({ ...state, attachment: t('support.tickets.uploadFailed') }));
        } finally {
            setFileLoading(false);
        }
    }

    const getAttachmentName = (url) => {
        if (!url) return '';
        try {
            const orig = String(url).split('/').pop() || '';
            const cleaned = orig.replace(/^\d+-[a-f0-9-]+-/, '');
            const parts = cleaned.split('.');
            if (parts.length <= 1) return cleaned || orig;
            const last = parts.pop();
            if (last && !isNaN(last) && parts.length > 0) return parts.join('.');
            return cleaned || orig;
        } catch {
            return String(url);
        }
    };

    const removeAttachment = async (attachmentUrl) => {
        if (!attachmentUrl) return;
        setDeleteLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(
                `${process.env.REACT_APP_UPLOADS_API_BASE_URL}/delete`,
                {
                    data: { keyFile: attachmentUrl },
                    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                    validateStatus: () => true
                }
            );
            if (response?.status >= 200 && response?.status < 300) {
                setAttchment((state) => (Array.isArray(state) ? state : [state]).filter((url) => url !== attachmentUrl));
                setErrors((state) => ({ ...state, attachment: undefined }));
                const input = document.getElementById(fileInputId);
                if (input) {
                    input.value = '';
                }
                return;
            }
            setErrors((state) => ({ ...state, attachment: t('support.tickets.uploadFailed') }));
        } catch {
            setErrors((state) => ({ ...state, attachment: t('support.tickets.uploadFailed') }));
        } finally {
            setDeleteLoading(false);
        }
    };

    useEffect(() => {
        if (productSelected?.value && !productOptions.some((item) => item.value === productSelected.value)) {
            setProductSelected(undefined);
        }
    }, [productOptions, productSelected]);

    useEffect(() => {
        if (sectionSelected?.value && !sectionOptions.some((item) => item.value === sectionSelected.value)) {
            setSectionSelected(undefined);
        }
    }, [sectionOptions, sectionSelected]);

    useEffect(() => {
        const prepareTicketNumber = async () => {
            if (!user?._id || reservedTicketNumber || reservedTicketRequestUserRef.current === user._id) return;
            reservedTicketRequestUserRef.current = user._id;
            try {
                const ticketNumberResponse = await generateTicketNumber(
                    `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/number/next`,
                    { createdBy: user._id || null }
                );
                const generatedTicket = ticketNumberResponse?.data || {};
                if (generatedTicket?.ticketNumber) {
                    setReservedTicketNumber(generatedTicket.ticketNumber);
                    setReservedUserSequence(generatedTicket.userSequence || 1);
                }
            } catch {
                setErrors((state) => ({
                    ...state,
                    ticketNumber: currentLangId === 'ar'
                        ? 'تعذر توليد رقم التذكرة تلقائياً حالياً'
                        : 'Unable to generate the ticket number automatically right now'
                }));
            }
        };

        prepareTicketNumber();
    }, [currentLangId, generateTicketNumber, reservedTicketNumber, user?._id]);



    const submitHandler = async () => {
        setErrors({});
        let _errors = {};
        const contentText = stripHtmlText(content);
        if(!sectionSelected?.value ){
            _errors.section = t('support.tickets.sectionRequired')
        }
        if(!productSelected?.value ){
            _errors.product = t('support.tickets.productRequired')
        }
        if(!prioritySelected?.value ){
            _errors.priority = t('support.tickets.priorityRequired')
        }
        // العنوان والمحتوى: لغة واحدة كاملة على الأقل، وأي لغة اتكتب فيها لازم تتكمل.
        const bilingual = validateBilingual([
            { key: 'subject', label: t('support.tickets.subject'), required: true, ar: subject, en: subjectEn },
            { key: 'content', label: t('support.tickets.content'), required: true, ar: content, en: contentEn },
        ], { isAr: currentLangId !== 'en', preferredLang: formLang });
        if (!bilingual.valid) {
            _errors.bilingual = bilingual;
            setFormLang(bilingual.firstLang);
        }
        if(fileLoading){
            _errors.attachment = t('support.tickets.uploadInProgress')
        }
        if (productOptions.length === 0) {
            _errors.product = currentLangId === 'ar'
                ? 'لا توجد خدمات متاحة لك حالياً. يرجى مراجعة الإدارة.'
                : 'No services are currently available for your account.';
        }
        if(Object.keys(_errors).length){
            setErrors(_errors);
            return;
        }

        try {
            const generatedTicket = reservedTicketNumber
                ? {
                    ticketNumber: reservedTicketNumber,
                    userSequence: reservedUserSequence || 1
                }
                : (await generateTicketNumber(
                    `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/number/next`,
                    {
                        createdBy: user._id || null
                    }
                ))?.data || {};
            if (!generatedTicket?.ticketNumber) {
                toast.error(currentLangId === 'ar' ? 'تعذر توليد رقم التذكرة، حاول مرة أخرى' : 'Unable to generate ticket number, please try again');
                return;
            }

            const _data = 
                {
                    ticketNumber: generatedTicket.ticketNumber,
                    customerName: user.name || null,
                    customerId: user._id || null,
                    email : user.email || null,
                    mobile: user.mobile || null,
                    subject: subject.trim() || subjectEn.trim() || null,
                    content : contentText ? content : (contentEn || null),
                    subjectEn: subjectEn.trim(),
                    contentEn: stripHtmlText(contentEn) ? contentEn : '',
                    visibility: 'external',
                    sectionId : sectionSelected.value || null,
                    ProductId: productSelected.value || null,
                    priority: prioritySelected.value || 'medium',
                    attachments : attchment.length ? attchment : null,
                    statusId: getDefaultStatusId(supportSettings?.statuses),
                    createdBy: user._id || null,
                    userSequence: generatedTicket.userSequence || 1,
                    branchId: currentBranchId || user?.branch_id || null,
                    companyId: currentCompanyId || user?.company_id || null,
                    tenantId: currentTenantId || user?.tenant_id || null,
                    ...(user?.role === 'admin' && ticketCreatedAt ? { createdAt: new Date(ticketCreatedAt).toISOString() } : {})
                }
            console.log(_data);
            await submitTicket(`${process.env.REACT_APP_SUPPORT_API_BASE_URL}`,_data);
        } catch (error) {
            // رسالة السيرفر نفسها لو موجودة، عشان يبان فين المشكلة.
            toast.error(error?.response?.data?.message || (currentLangId === 'ar' ? 'فشل إنشاء التذكرة، حاول مرة أخرى' : 'Failed to create the ticket, please try again'));
        }
    }
    useEffect(() => {
        if(resultSubmit){
            const createdTicketId = resultSubmit?.data?._id;
            const createdTicketNo = resultSubmit?.data?.ticketNumber;
            setCreatedTicketNumber(createdTicketNo || null);
            toast.success(
                createdTicketNo
                    ? (currentLangId === 'ar'
                        ? `تم إنشاء التذكرة رقم ${createdTicketNo} بنجاح`
                        : `Ticket #${createdTicketNo} created successfully`)
                    : t('support.tickets.createdToast')
            );
            if (createdTicketId) {
                const timeout = setTimeout(() => {
                    navigate(`/${params.tenant}/tickets/show/${createdTicketId}`);
                }, 1300);
                return () => clearTimeout(timeout);
            }
        }
    },[currentLangId, navigate, params.tenant, resultSubmit, t]);

    return (
        <div className='relative'>
            <Toaster
                position="top-center"
                containerStyle={{
                    top: 60,
                    zIndex: 9999,
                }}
                toastOptions={{
                    duration: 1300,
                    hideProgressBar: false,
                    closeOnClick: true,
                    draggable: true,
                    pauseOnHover: true,
                    style: {
                        direction: currentLangId === 'ar' ? 'rtl' : 'ltr',
                        zIndex: 9999,
                    },
                }}
            />
            <div>
                <CardHeaderWithActions title={t('support.tickets.createTitle')}>
                    {loadingSubmit ? <MwSpinnerButton/> :<MwButton onClick={submitHandler} type='saveBtn' disabled={fileLoading}>{t('support.tickets.send')}</MwButton>}
                </CardHeaderWithActions>
                <div className='flex flex-col gap-3'>
                    {createdTicketNumber && (
                        <AlertMsg>
                            {(currentLangId === 'ar'
                                ? `تم توليد وإنشاء رقم التذكرة: ${createdTicketNumber}`
                                : `Generated ticket number: ${createdTicketNumber}`)}
                        </AlertMsg>
                    )}
                    <MwInputText
                        label={t('support.tickets.ticketNumber')}
                        value={reservedTicketNumber || ''}
                        readOnly
                        disabled
                        placeholder={currentLangId === 'ar' ? 'جاري توليد رقم التذكرة...' : 'Generating ticket number...'}
                        invalid={!!errors?.ticketNumber}
                        invalidMsg={errors?.ticketNumber}
                    />
                    <MwSelector
                        className={` z-50`}
                        label={t('support.tickets.product')}
                        initalValue={t('support.tickets.selectProductPlaceholder')}
                        dataType={'labelValue'}
                        _data={productOptions}
                        selectedItem={productSelected}
                        setSelectedItem={setProductSelected}
                        withAddNew={false}
                        invalid={!!errors?.product}
                        invalidMsg={errors?.product}
                        />
                    <MwSelector
                        className={` z-10`}
                        label={t('support.tickets.section')}
                        initalValue={t('support.tickets.selectSectionPlaceholder')}
                        dataType={'labelValue'}
                        _data={sectionOptions}
                        selectedItem={sectionSelected}
                        setSelectedItem={setSectionSelected}
                        withAddNew={false}
                        invalid={!!errors?.section}
                        invalidMsg={errors?.section}
                        />
                    <MwSelector
                        className={` z-[9]`}
                        label={t('support.tickets.priority')}
                        initalValue={t('support.tickets.selectPriorityPlaceholder')}
                        dataType={'labelValue'}
                        _data={priorityOptions}
                        selectedItem={prioritySelected}
                        setSelectedItem={setPrioritySelected}
                        withAddNew={false}
                        invalid={!!errors?.priority}
                        invalidMsg={errors?.priority}
                        />
                    <LangSwitch
                        value={formLang}
                        onChange={setFormLang}
                        isAr={currentLangId !== 'en'}
                        filled={{ ar: Boolean(subject.trim() || stripHtmlText(content)), en: Boolean(subjectEn.trim() || stripHtmlText(contentEn)) }}
                        errors={errors?.bilingual?.errors}
                        messages={errors?.bilingual?.messages}
                    />
                    <MwInputText
                        label ={withLangLabel(t('support.tickets.subject'), formLang, currentLangId !== 'en')}
                        value = {formLang === 'ar' ? subject : subjectEn}
                        onChange={(e) => (formLang === 'ar' ? setSubject : setSubjectEn)(e.target.value)}
                        classNameInput="h-[38px] border border-slate-200 hover:border-slate-300 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                        invalid={!!errors?.bilingual?.errors?.[formLang]?.subject}
                        invalidMsg={errors?.bilingual?.errors?.[formLang]?.subject}
                        />
                    {user?.role === 'admin' && (
                        <MwInputText
                            label={currentLangId === 'ar' ? 'تاريخ التذكرة' : 'Ticket Date'}
                            inputType="datetime-local"
                            value={ticketCreatedAt}
                            onChange={(e) => setTicketCreatedAt(e.target.value)}
                        />
                    )}
                    {['ar', 'en'].map((code) => (
                        <div key={code} className={formLang === code ? '' : 'hidden'}>
                            <MwTextArea
                                label ={withLangLabel(t('support.tickets.content'), code, currentLangId !== 'en')}
                                rows={7}
                                value = {code === 'ar' ? content : contentEn}
                                onChange={(e) => (code === 'ar' ? setContent : setContentEn)(e.target.value)}
                                invalid={!!errors?.bilingual?.errors?.[code]?.content}
                                invalidMsg={errors?.bilingual?.errors?.[code]?.content}
                                dir={code === 'ar' ? 'rtl' : 'ltr'}
                                rich
                                />
                        </div>
                    ))}
                    <MwInputFile
                        label={t('support.tickets.attachFile')}
                        onChangeHandler={uploadHandler}
                        acceptTypes='*/*'
                        loading= {fileLoading}
                        disabled={fileLoading || deleteLoading}
                        id={fileInputId}
                        multiple
                        />
                    {attchment.map((attachmentUrl) => (
                        <div key={attachmentUrl} className="flex items-center justify-between gap-3">
                            <a
                                href={attachmentUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs font-semibold text-slate-600 hover:text-[#3D3B40] truncate"
                            >
                                {getAttachmentName(attachmentUrl)}
                            </a>
                            <button
                                type="button"
                                onClick={() => removeAttachment(attachmentUrl)}
                                disabled={fileLoading || deleteLoading}
                                className="p-1 hover:bg-red-50 rounded-full text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
                            >
                                <FaTimes size={14} />
                            </button>
                        </div>
                    ))}
                    {errors?.attachment && <p className='text-xs text-red-500'>{errors.attachment}</p>}
                    {!errors?.attachment && fileLoading && <p className='text-xs text-slate-500'>{t('support.tickets.uploadInProgress')}</p>}
                </div>
            </div>
        </div>
    )
}

export default TicketCreate
